import { Checkbox } from '@solvhealth/jigsaw';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { formShape } from 'rc-form';
import { useMemo } from 'react';
import Booking from '@solvhealth/types/interfaces/Booking';
import styled from 'styled-components';
import { GenericObj } from '@solvhealth/types/interfaces/generics';
import { CheckboxMeta, useEvent, usePaperworkResponses } from '../hooks';
import { COPY_PAPERWORK_FIELDS_CHECKBOXES } from '../interfaces/CopyPaperworkFieldsCheckbox';
import { PaperworkCopyFormFieldsCheckboxName, PaperworkFieldName } from '../paperworkConstants';
import { useSolvSelector } from '~/reducers';
import { useQueryFamilyBookings } from '../../../hooks/useQueryFamilyBookings';
import { validateFamilyBooking } from '../../../util/validateFamilyBooking';

const StyledCheckboxRowContainer = styled.div<{ $noTopMargin?: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${(props) => (props.$noTopMargin ? '0px' : 'calc(-2rem / 3)')};
`;

enum CheckboxState {
  UNINITIALIZED, // initial checkbox state
  FALSE_NOOP, // once the form is populated (if there are any fields autocomplete), this indicates that the checkbox should be false and _not_ clear the form
  FALSE, // when set to FALSE, the form will clear and call toggleSideEffect(false)
  TRUE, // when set to TRUE, the form will populate with primaryPaperworkData or prefilledData (TODO) and call toggleSideEffect(true)
  DISABLED, // if there are no autofill fields available, the checkbox will be DISABLED and will not render
}

type Props = {
  /**
   * Enum from PaperworkCopyFormFieldsCheckboxName to refer to a specific checkbox, used to get the fields to copy
   * from COPY_PAPERWORK_FIELDS_CHECKBOXES and for the checkbox label from paperwork.lang.ts */
  checkboxName: PaperworkCopyFormFieldsCheckboxName;

  /** Form object */
  form: formShape;

  /** Optional side effect function that is called with true or false when the checkbox is checked or unchecked */
  toggleSideEffect?: (arg0: boolean) => any;

  /** Booking object */
  booking: Booking;

  /**
   * Optional second source of truth for if the checkbox is enabled, used for situations like copying insurance where
   * the information about if there are fields to copy comes from another source than the primary paperwork (i.e. if
   * there is an insurance image that is able to be copied over but none of the fields have been filled out yet)
   */
  checkboxEnabled?: boolean;

  checkboxMeta?: CheckboxMeta;

  /** For styling, checkbox as the first form field has different margin than a checkbox in a different position */
  noTopMargin?: boolean;
};

const sameAsPrimaryData = (
  currentFormFieldValues: GenericObj,
  primaryResponses: GenericObj,
  fieldsToCopy: PaperworkFieldName[]
) => {
  let sameAsPrimary = true;
  if (fieldsToCopy.length === 0) {
    return false;
  }
  fieldsToCopy.forEach((field) => {
    if (currentFormFieldValues[field] && primaryResponses[field]) {
      if (currentFormFieldValues[field] !== primaryResponses[field]) {
        sameAsPrimary = false;
      }
    } else {
      if (currentFormFieldValues[field] || primaryResponses[field]) {
        sameAsPrimary = false;
      }
    }
  });
  return sameAsPrimary;
};

const usePrimaryPaperworkResponses = (
  bookingId: string,
  shouldCopyFromCurrentBooking: boolean = false
) => {
  const primaryPaperworkResponses = useSolvSelector((state) => state.paperworkMeta.formData);
  const shouldNotFetch = useMemo(
    () => !isEmpty(primaryPaperworkResponses) || !bookingId || shouldCopyFromCurrentBooking,
    [primaryPaperworkResponses, bookingId, shouldCopyFromCurrentBooking]
  );
  const {
    data: primaryPaperworkData,
    isLoading,
    isError,
  } = usePaperworkResponses(shouldNotFetch ? null : bookingId);
  return {
    primaryPaperworkData: isEmpty(primaryPaperworkResponses)
      ? primaryPaperworkData
      : primaryPaperworkResponses,
    isLoading,
    isError,
  };
};

const PaperworkCopyFormFieldsCheckbox = (props: Props) => {
  const { checkboxName, form, booking, toggleSideEffect, checkboxMeta, noTopMargin } = props;
  const formRef = useRef(form);
  const { t } = useTranslation('paperwork');

  const [checkboxState, setCheckboxState] = useState(CheckboxState.UNINITIALIZED);
  const hasPrefilledData = useRef(false);
  const prefilledData = useRef({} as { [key in PaperworkFieldName]?: any });

  const paperwork = useSolvSelector((state) => state.paperwork);
  const { familyId } = validateFamilyBooking(booking);
  const { primaryBooking } = useQueryFamilyBookings(familyId);
  const primaryName = primaryBooking?.first_name ?? primaryBooking?.firstName;
  const primaryBookingId = primaryBooking?.id;
  const currentBookingName = paperwork.formData[PaperworkFieldName.PATIENT_FIELD_FIRST_NAME];

  const {
    fieldNames: checkboxCopyFieldNames,
    defaultChecked,
    fieldNamesToCopyFromCurrentBooking,
  } = COPY_PAPERWORK_FIELDS_CHECKBOXES.get(checkboxName) || {};
  const shouldCopyFromCurrentBooking = !!fieldNamesToCopyFromCurrentBooking;
  const keyString = `checkboxes.${checkboxName}`;
  const copy = t(keyString as any, {
    name: shouldCopyFromCurrentBooking ? currentBookingName : primaryName,
  });

  const { primaryPaperworkData, isLoading, isError } = usePrimaryPaperworkResponses(
    primaryBookingId,
    shouldCopyFromCurrentBooking
  );

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const onInitialized = useEvent(() => {
    if (checkboxState !== CheckboxState.UNINITIALIZED) {
      return;
    }
    const formFieldNames = Object.keys(formRef.current.getFieldsValue());
    const shouldInitialize =
      shouldCopyFromCurrentBooking ||
      checkboxCopyFieldNames?.some((fieldName) => formFieldNames.includes(fieldName));

    if (!shouldInitialize) {
      return;
    }
    const fieldValues = formRef.current.getFieldsValue();
    if (
      checkboxCopyFieldNames?.some(
        (fieldName) => fieldValues[fieldName] && fieldValues[fieldName] !== ''
      )
    ) {
      //Form has data prefilled
      if (
        checkboxCopyFieldNames.every((fieldName) => {
          if (!primaryPaperworkData[fieldName] && !fieldValues[fieldName]) {
            return true;
          }
          return fieldValues[fieldName] === primaryPaperworkData[fieldName];
        })
      ) {
        //If fields match primary paperwork fields, box should appear checked
        setCheckboxState(CheckboxState.TRUE);
      } else if (fieldNamesToCopyFromCurrentBooking) {
        //in the case that the fields are to be copied over from within the same booking's paperwork, check to see if the fields match the autofilled fields so we can auto-set to true
        let shouldInitializeChecked = true;
        let currentBookingFieldValue;
        let currentPageFieldValue;
        let fieldName;
        for (let i = 0; i < fieldNamesToCopyFromCurrentBooking.length; i++) {
          fieldName = fieldNamesToCopyFromCurrentBooking[i];
          currentBookingFieldValue = paperwork.formData[fieldName];
          currentPageFieldValue = fieldValues[checkboxCopyFieldNames[i]];
          if (currentBookingFieldValue || currentPageFieldValue) {
            if (currentBookingFieldValue !== currentPageFieldValue) {
              shouldInitializeChecked = false;
              break;
            }
          }
        }
        setCheckboxState(shouldInitializeChecked ? CheckboxState.TRUE : CheckboxState.FALSE_NOOP);
      } else {
        //default to checkbox unchecked, but don't clear form
        setCheckboxState(CheckboxState.FALSE_NOOP);
        hasPrefilledData.current = true;
        prefilledData.current = fieldValues;
      }
    } else {
      hasPrefilledData.current = false;
      let shouldBeChecked = defaultChecked;
      if (checkboxMeta !== undefined) {
        shouldBeChecked = !checkboxMeta.checkHasInsuranceFields();
      }
      setCheckboxState(shouldBeChecked ? CheckboxState.TRUE : CheckboxState.FALSE);
    }
  });

  const hasAutofillFieldsAvailable: boolean = useMemo(() => {
    if (checkboxMeta?.checkboxEnabled) {
      return true;
    }
    if (fieldNamesToCopyFromCurrentBooking) {
      //To copy from the current profile, check that the fields to copy over exist
      if (!fieldNamesToCopyFromCurrentBooking.some((fieldName) => paperwork.formData[fieldName])) {
        return false;
      }
      //And check that the fields to copy into exist
      return !!checkboxCopyFieldNames?.some(
        (fieldName) => !!paperwork.standardFields?.find((field) => field.field_name === fieldName)
      );
    }
    if (checkboxCopyFieldNames && !isEmpty(primaryPaperworkData)) {
      return checkboxCopyFieldNames.some((fieldName) => primaryPaperworkData[fieldName]);
    }
    return false;
  }, [
    primaryPaperworkData,
    fieldNamesToCopyFromCurrentBooking,
    paperwork,
    checkboxMeta?.checkboxEnabled,
    checkboxCopyFieldNames,
  ]);

  useEffect(() => {
    if (!hasAutofillFieldsAvailable) {
      setCheckboxState(CheckboxState.DISABLED);
    } else {
      if (hasPrefilledData.current) {
        const fieldValues = formRef.current.getFieldsValue();
        if (sameAsPrimaryData(fieldValues, primaryPaperworkData, checkboxCopyFieldNames ?? [])) {
          setCheckboxState(CheckboxState.TRUE);
        } else {
          setCheckboxState(CheckboxState.FALSE_NOOP);
        }
      } else {
        if (checkboxMeta !== undefined) {
          setCheckboxState(
            checkboxMeta.checkHasInsuranceFields() ? CheckboxState.FALSE : CheckboxState.TRUE
          );
        } else {
          setCheckboxState(CheckboxState.TRUE);
        }
      }
    }
  }, [hasAutofillFieldsAvailable, primaryPaperworkData, checkboxCopyFieldNames, checkboxMeta]);

  const updateFields = useEvent((shouldFillFields: boolean = true) => {
    const updatedFields = {} as { [key in PaperworkFieldName]?: string };
    const formFieldNames = Object.keys(formRef.current.getFieldsValue());
    checkboxCopyFieldNames?.forEach((fieldName, i) => {
      if (formFieldNames.includes(fieldName)) {
        if (fieldNamesToCopyFromCurrentBooking) {
          const fieldNameToCopyFrom = fieldNamesToCopyFromCurrentBooking[i];
          if (shouldFillFields) {
            updatedFields[fieldName] = paperwork.formData[fieldNameToCopyFrom];
          } else {
            updatedFields[fieldName] = hasPrefilledData.current
              ? prefilledData.current[fieldName]
              : '';
          }
        } else {
          if (shouldFillFields) {
            updatedFields[fieldName] = primaryPaperworkData[fieldName];
          } else {
            updatedFields[fieldName] = hasPrefilledData.current
              ? prefilledData.current[fieldName]
              : '';
          }
        }
      }
    });
    formRef.current.setFieldsValue(updatedFields);
  });

  useEffect(() => {
    if (checkboxState === CheckboxState.TRUE) {
      if (toggleSideEffect) {
        toggleSideEffect(true);
      }
    } else if (checkboxState === CheckboxState.FALSE) {
      if (toggleSideEffect) {
        toggleSideEffect(false);
      }
    } else if (checkboxState === CheckboxState.UNINITIALIZED) {
      onInitialized();
    }
  }, [checkboxState, toggleSideEffect, onInitialized]);

  const handleChange = (e: React.ChangeEvent) => {
    setCheckboxState((prevState) =>
      prevState === CheckboxState.TRUE ? CheckboxState.FALSE : CheckboxState.TRUE
    );
  };

  useEffect(() => {
    if (
      checkboxState === CheckboxState.UNINITIALIZED ||
      checkboxState === CheckboxState.FALSE_NOOP ||
      checkboxState === CheckboxState.DISABLED
    ) {
      return;
    }
    if (
      (!isEmpty(primaryPaperworkData) ||
        (isEmpty(primaryPaperworkData) && shouldCopyFromCurrentBooking)) &&
      !isLoading &&
      hasAutofillFieldsAvailable
    ) {
      updateFields(checkboxState === CheckboxState.TRUE);
    }
  }, [
    checkboxState,
    primaryPaperworkData,
    shouldCopyFromCurrentBooking,
    isLoading,
    hasAutofillFieldsAvailable,
    updateFields,
  ]);

  const checkboxChecked = useMemo(() => {
    return checkboxState === CheckboxState.TRUE;
  }, [checkboxState]);

  if (
    isLoading ||
    isError ||
    !hasAutofillFieldsAvailable ||
    (isEmpty(primaryPaperworkData) && !shouldCopyFromCurrentBooking) ||
    checkboxState === CheckboxState.DISABLED
  ) {
    return null;
  }

  return (
    <StyledCheckboxRowContainer $noTopMargin={noTopMargin}>
      <Checkbox checked={checkboxChecked} label={copy} onChange={handleChange} size="medium" />
    </StyledCheckboxRowContainer>
  );
};

export default PaperworkCopyFormFieldsCheckbox;
